<template>
  <div class="flowchart">
    <div class="row">
      <div class="col-12">
        <div class="d-flex justify-content-end px-1">
          <div class="flowchart-toolbar mb-1" role="group" aria-label="Flowchart zoom">
            <button type="button" :title="$t('flowchart.zoom_in')" :aria-label="$t('flowchart.zoom_in')" @click="zoomIn">
              <feather-icon icon="ZoomInIcon" size="20" />
            </button>
            <button type="button" class="flowchart-toolbar__arrange" :disabled="isArranging" :title="$t('flowchart.arrange_nodes')" :aria-label="$t('flowchart.arrange_nodes')" :aria-busy="isArranging" @click="arrangeNodes">
              <feather-icon icon="GitBranchIcon" size="20" />
            </button>
            <button type="button" :title="$t('flowchart.fit_view')" :aria-label="$t('flowchart.fit_view')" @click="fitView">
              <feather-icon icon="MaximizeIcon" size="20" />
            </button>
            <button type="button" :title="$t('flowchart.zoom_out')" :aria-label="$t('flowchart.zoom_out')" @click="zoomOut">
              <feather-icon icon="ZoomOutIcon" size="20" />
            </button>
            <span class="flowchart-toolbar__zoom">{{ zoomPercentage }}%</span>
          </div>
        </div>
      </div>
      <div class="col-md-2">
        <b-form-input v-model="search" class="mb-3" :placeholder="$t('search')"/>
        <div style="max-height: 500px; overflow: auto">
          <command-list
            class="mb-3"
            :list="baseCommands"
            :search="search"
            :title="$t('scripts.base_commands')"
            @node-drag-start="onNodeDragStart"
          />
          <command-list
            :list="customCommands"
            :search="search"
            :title="$t('scripts.custom_commands')"
            @node-drag-start="onNodeDragStart"
          />
        </div>
      </div>
      <div class="col-md-10">
        <div id="drawflow" @drop="drop($event)" @dragover="allowDrop($event)"></div>
      </div>
    </div>
    <command-config-modal
      :current-node-info="currentNodeInfo"
      :current-node-requirements="currentNodeRequirements"
      :flowchart-config="config"
      :value="currentNodeStorage"
      @input="saveCurrentNodeStorage"
    />
  </div>
</template>

<script>
import Vue from "vue";
import OmniviewDrawflow from "@/libs/drawflow/OmniviewDrawflow";
import Node from "@/components/flowchart/nodes/commands/Node.vue";
import Delay from "@/components/flowchart/nodes/commands/Delay.vue";
import AIDecision from "@/components/flowchart/nodes/commands/AIDecision.vue";
import NodeData from "@/components/flowchart/nodes/commands/NodeData";
import CommandConfigModal from "./CommandConfigModal";
import {EventBus} from "@/libs/event-bus";
import CommandList from "./CommandList.vue";
import {AIDecisionNodeFields} from "@/components/flowchart/nodes/commands/BaseNodeFields";
import { mergeNotificationSubjectRequirement } from "@/libs/CommandFields/notificationCommand";

export default {
  components: {
    NodeData,
    CommandConfigModal,
    CommandList,
  },
  props: {
    commandOptions: Array,
    flowchartData: Array,
    config: Object,
    missingNodeData: Array,
  },
  data() {
    return {
      editor: null,
      mobile_item_select: "",
      mobile_last_move: null,
      baseNodeList: [
        {
          _id: 1000,
          name: "delay",
          label: this.$t(`commands.list.delay.name`),
          item: "Delay_1000",
          data: {},
          input: 1,
          output: 1,
        },
        {
          _id: 1001,
          name: "ai_decision",
          label: "AI Decision",
          item: "AIDecision_1001",
          data: {
            storage: null,
            config: {
              custom_outputs: [
                {
                  id: "output_1",
                  text: "Yes",
                  "color": "#ff0000"
                },
                {
                  id: "output_2",
                  text: "No",
                  color: "#00ff00"
                }
              ]
            },
            requirements: AIDecisionNodeFields()
          },
          input: 1,
          output: 2,
        },
      ],
      nodeList: [],
      currentNodeId: null,
      currentNodeRequirements: null,
      currentNodeData: null,
      currentNodeStorage: null,
      showNodeData: false,
      search: "",
      currentZoom: 1,
      isArranging: false,
    };
  },
  async mounted() {
    const id = document.getElementById("drawflow");
    Vue.prototype.$df = new OmniviewDrawflow(id, Vue, this);
    this.editor = this.$df;
    this.editor.useuuid = true;
    this.editor.on("nodeSelected", this.onNodeSelected);
    this.editor.on("nodeUnselected", this.onNodeUnselected);
    this.editor.on("nodeRemoved", this.onNodeUnselected);
    this.editor.on("zoom", this.onZoomChanged);
    //this.editor.on("connectionCreated", this.checkNodesIOs);
    //this.editor.on("connectionRemoved", this.checkNodesIOs);
    //this.editor.on("import", this.checkNodesIOs);

    this.editor.start();
    this.buildNodeList();
    this.initDraggableNodes();

    this.editor.registerNode("Node", Node, {}, {});
    this.editor.registerNode("Delay", Delay, {}, {});
    this.editor.registerNode("AIDecision", AIDecision, {}, {});

    this.$emit("flowchart-ready", this.editor);

    EventBus.on("node:open-modal", this.openConfigModal);
  },
  beforeDestroy() {
    EventBus.off("node:open-modal", this.openConfigModal);
    this.editor?.destroy();
  },
  watch: {
    commandOptions() {
      this.buildNodeList();
    },
    missingNodeData(value) {
      if (value.length) {
        value.forEach((item) => {
          const node = document.querySelector(`#node-${item.id}`);
          node.classList.add("error");
        });
      } else {
        const nodes = document.querySelectorAll(".drawflow-node.Node");
        nodes.forEach((node) => node.classList.remove("error"));
      }
    },
  },
  computed: {
    zoomPercentage() {
      return Math.round(this.currentZoom * 100);
    },
    baseCommands() {
      return this.nodeList.filter((node) => node.data && node.data.cmd !== "custom");
    },
    customCommands() {
      return this.nodeList.filter((node) => node.data && node.data.cmd === "custom");
    },
    currentNodeInfo() {
      if (!this.currentNodeData) return {};
      const data = this.currentNodeData.data || {};
      const cmdSlug = data.cmd ?? data.command ?? data.cmd_name;
      return {
        id: this.currentNodeData.id,
        name: data.label,
        command: cmdSlug,
        cmd: cmdSlug,
      };
    },
  },
  methods: {
    onZoomChanged(zoom) {
      this.currentZoom = zoom;
    },
    zoomIn() {
      this.editor?.zoomIn();
    },
    zoomOut() {
      this.editor?.zoomOut();
    },
    fitView() {
      this.editor?.fitView();
    },
    async arrangeNodes() {
      if (!this.editor || this.isArranging) return;

      this.isArranging = true;
      try {
        await this.editor.arrangeNodes();
      } finally {
        this.isArranging = false;
      }
    },
    saveCurrentNodeStorage(storage) {
      if (!this.currentNodeId) return;

      const data = this.editor.commitNodeData(this.currentNodeId, { storage });
      if (!data) return;

      this.currentNodeStorage = data.storage;
      this.currentNodeData = { ...this.currentNodeData, data };
      EventBus.emit("node:data-updated", this.currentNodeId);
    },
    buildNodeList() {
      this.nodeList = JSON.parse(JSON.stringify(this.baseNodeList));
      this.nodeList = this.nodeList.concat(
        this.commandOptions.map((cmd) => {

          return {
            _id: cmd.value,
            name: cmd.label,
            label:
              cmd.cmd !== "custom" ? this.$t(`commands.list.${cmd.label}.name`) : cmd.label,
            item: `Node_${cmd.value}`,
            input: 1,
            output: 1,
            data: {...cmd, storage: null},
          };
        })
      );
    },
    onNodeSelected(id) {
      this.currentNodeId = id;
      this.currentNodeData = this.editor.getNodeFromId(id);
      if (this.currentNodeData.data.requirements) {
        this.currentNodeRequirements = this.currentNodeData.data.requirements;
        this.currentNodeStorage = this.currentNodeData.data.storage ?? null;
      }
    },
    onNodeUnselected() {
      this.currentNodeId = null;
      this.currentNodeData = null;
      this.currentNodeRequirements = null;
      this.currentNodeStorage = null;
      // this.showNodeData = false;
    },
    syncNotificationSubjectRequirement() {
      const currentNode = this.editor.getNodeFromId(this.currentNodeId);
      const nodeData = currentNode?.data;
      const updatedNodeData = mergeNotificationSubjectRequirement(
        nodeData,
        this.commandOptions || []
      );
      if (!nodeData || updatedNodeData === nodeData) return;

      this.editor.updateNodeDataFromId(this.currentNodeId, updatedNodeData);
      this.currentNodeData = this.editor.getNodeFromId(this.currentNodeId);
    },
    async openConfigModal(nodeId) {
      this.currentNodeId = nodeId;
      this.currentNodeData = this.editor.getNodeFromId(nodeId);
      if (this.currentNodeData.data.requirements) {
        this.syncNotificationSubjectRequirement();
        this.currentNodeRequirements = this.currentNodeData.data.requirements;
        this.currentNodeStorage = this.currentNodeData.data.storage ?? null;
        await this.$nextTick();
        this.$root.$emit("bv::show::modal", "cmd_config_modal");
      }
    },
    async checkNodesIOs() {
      await this.$nextTick();

      const nodes = Object.values(this.editor.export().drawflow.Home.data);
      nodes.forEach((node) => {
        const currentNode = document.getElementById(`node-${node.id}`);
        const outputs = currentNode.querySelector(".outputs");
        if (node.outputs.output_1.connections.length > 0) {
          outputs.classList.add("prevent-new-outputs");
        } else {
          outputs.classList.remove("prevent-new-outputs");
        }
      });
    },
    initDraggableNodes() {
      const elements = document.getElementsByClassName("drag-drawflow");
      for (let i = 0; i < elements.length; i++) {
        elements[i].addEventListener("touchend", this.drop, false);
        elements[i].addEventListener("touchmove", this.positionMobile, false);
        elements[i].addEventListener("touchstart", this.drag, false);
      }
    },
    onNodeDragStart(data) {
      this.mobile_item_select = data;
    },
    drop(ev) {
      if (ev.type === "touchend") {
        var parentdrawflow = document
          .elementFromPoint(
            this.mobile_last_move.touches[0].clientX,
            this.mobile_last_move.touches[0].clientY
          )
          .closest("#drawflow");
        if (parentdrawflow != null) {
          this.addNodeToDrawFlow(
            this.mobile_item_select,
            this.mobile_last_move.touches[0].clientX,
            this.mobile_last_move.touches[0].clientY
          );
        }
        this.mobile_item_select = "";
      } else {
        ev.preventDefault();
        var data = ev.dataTransfer.getData("node");
        this.addNodeToDrawFlow(data, ev.clientX, ev.clientY);
      }
    },
    positionMobile(ev) {
      this.mobile_last_move = ev;
    },
    allowDrop(ev) {
      ev.preventDefault();
    },
    addNodeToDrawFlow(nodeName, pos_x, pos_y) {
      pos_x =
        pos_x *
        (this.editor.precanvas.clientWidth /
          (this.editor.precanvas.clientWidth * this.editor.zoom)) -
        this.editor.precanvas.getBoundingClientRect().x *
        (this.editor.precanvas.clientWidth /
          (this.editor.precanvas.clientWidth * this.editor.zoom));
      pos_y =
        pos_y *
        (this.editor.precanvas.clientHeight /
          (this.editor.precanvas.clientHeight * this.editor.zoom)) -
        this.editor.precanvas.getBoundingClientRect().y *
        (this.editor.precanvas.clientHeight /
          (this.editor.precanvas.clientHeight * this.editor.zoom));

      const [name, id] = nodeName.split("_");
      const nodeSelected = this.nodeList.find((ele) => ele._id == id);

      this.editor.addNode(
        nodeSelected.data ? nodeSelected.data.label : nodeSelected.name,
        nodeSelected.input,
        nodeSelected.output,
        pos_x,
        pos_y,
        name,
        nodeSelected.data || {},
        name,
        "vue"
      );
    },
  },
};
</script>

<style lang="scss">
.node-data-config {
  margin-bottom: 10px;
  min-height: 65px;
  background-color: #fff;
  transition: all 0.4s;
  opacity: 0;

  &.show {
    opacity: 1;
  }
}
</style>
